import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  TableSortLabel,
  useMediaQuery,
  useTheme
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL, NTN } from '../constants/config';
import { usePermissions } from '../hooks/usePermissions';
import jsPDF from 'jspdf';
import ColumnFilter, { FilterConfig } from './ColumnFilter';
import ReportFilterBar from './ReportFilterBar';
import { FilterFieldMetadata, hasAtLeastOneFilter, optionsFromValues } from '../utils/filterUtils';

interface Bill {
  id: number;
  shopname: string;
  username: string | null;
  createdat: string;
  items: {
    itemname: string;
    quantity: number;
    pricePerUnit: number;
    discount: number;
  }[];
  orderType: string;
  paymentMethod: string;
  deliveryCharges: number;
  gstAmount: number;
  applyGstOnDelivery: boolean;
  totalAmount: number;
  cardDiscountBankName?: string | null;
  cardDiscountStartingDigits?: string | null;
  cardDiscountPercentage?: number | null;
  cardDiscountAmount?: number | null;
}

const Bills: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role') || '';
  const { hasPermission } = usePermissions(role);
  
  const [bills, setBills] = useState<Bill[]>([]);
  const [isLoadingBills, setIsLoadingBills] = useState(false);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filterMetadata, setFilterMetadata] = useState<Record<string, FilterFieldMetadata> | null>(null);
  const [showBillDetailsDialog, setShowBillDetailsDialog] = useState(false);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<keyof Bill>('createdat');
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});

  const getImplicitBillScope = () => {
    const shopName = localStorage.getItem('shopName');
    const userRole = localStorage.getItem('role') || '';
    const params: Record<string, string> = {};
    if (shopName) {
      params.shopname = shopName;
    }
    if (shopName && userRole !== 'Administrator' && userRole !== 'Director') {
      const fiveDaysAgo = new Date();
      fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
      params.startDate = fiveDaysAgo.toISOString().slice(0, 19).replace('T', ' ');
    }
    return params;
  };

  const getItemsSubtotal = (bill: Bill) => bill.items.reduce(
    (sum, item) => sum + (item.quantity * item.pricePerUnit * (1 - item.discount / 100)),
    0
  );

  const getBillTotal = (bill: Bill) =>
    getItemsSubtotal(bill) - Number(bill.cardDiscountAmount || 0) + bill.deliveryCharges + bill.gstAmount;

  const columnFilterConfigs: FilterConfig[] = [
    {
      field: 'id',
      type: 'number',
      label: 'ID',
      placeholder: 'Filter by ID',
      range: true,
      min: filterMetadata?.id?.min,
      max: filterMetadata?.id?.max
    },
    {
      field: 'createdat',
      type: 'date',
      label: 'Date',
      min: filterMetadata?.createdat?.min,
      max: filterMetadata?.createdat?.max
    },
    {
      field: 'shopname',
      type: 'select',
      label: 'Shop',
      placeholder: 'Filter by shop name',
      autocomplete: true,
      options: optionsFromValues(filterMetadata?.shopname?.values)
    },
    {
      field: 'orderType',
      type: 'select',
      label: 'Order Type',
      options: optionsFromValues(filterMetadata?.orderType?.values)
    },
    {
      field: 'paymentMethod',
      type: 'select',
      label: 'Payment Method',
      options: optionsFromValues(filterMetadata?.paymentMethod?.values)
    },
    {
      field: 'totalAmount',
      type: 'number',
      label: 'Total Amount',
      placeholder: 'Filter by amount',
      range: true,
      min: filterMetadata?.totalAmount?.min,
      max: filterMetadata?.totalAmount?.max
    }
  ];

  useEffect(() => {
    if (token) {
      fetchFilterMetadata();
    }
  }, [token]);

  const fetchFilterMetadata = async () => {
    try {
      setIsLoadingMetadata(true);
      const response = await axios.get(`${API_BASE_URL}/bills/filter-metadata`, {
        headers: { Authorization: token },
        params: getImplicitBillScope()
      });
      setFilterMetadata(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching bill filter metadata:', err);
      }
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const buildBillsQueryParams = () => {
    const params: Record<string, any> = { ...getImplicitBillScope() };

    if (columnFilters.id?.min !== undefined && columnFilters.id?.min !== '') {
      params.idMin = columnFilters.id.min;
    }
    if (columnFilters.id?.max !== undefined && columnFilters.id?.max !== '') {
      params.idMax = columnFilters.id.max;
    }
    if (columnFilters.createdat?.start && columnFilters.createdat?.end) {
      params.start = `${columnFilters.createdat.start} 00:00:00`;
      params.end = `${columnFilters.createdat.end} 23:59:59`;
    }
    if (!params.shopname && columnFilters.shopname) {
      params.shopname = columnFilters.shopname;
    }
    if (columnFilters.orderType) {
      params.orderType = columnFilters.orderType;
    }
    if (columnFilters.paymentMethod) {
      params.paymentMethod = columnFilters.paymentMethod;
    }
    if (columnFilters.totalAmount?.min !== undefined && columnFilters.totalAmount?.min !== '') {
      params.totalMin = columnFilters.totalAmount.min;
    }
    if (columnFilters.totalAmount?.max !== undefined && columnFilters.totalAmount?.max !== '') {
      params.totalMax = columnFilters.totalAmount.max;
    }
    return params;
  };

  const fetchBills = async () => {
    try {
      setIsLoadingBills(true);
      const response = await axios.get(`${API_BASE_URL}/bills`, {
        headers: { Authorization: token },
        params: buildBillsQueryParams()
      });
      setBills(response.data.map((bill: Bill) => ({ ...bill, totalAmount: getBillTotal(bill) })));
      setHasSearched(true);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching bills:', err);
      }
    } finally {
      setIsLoadingBills(false);
    }
  };

  const handleSearch = () => {
    fetchBills();
  };

  const handleClearFilters = () => {
    setColumnFilters({});
    setBills([]);
    setHasSearched(false);
  };

  // Utility function to convert UTC date string to browser's local timezone
  const convertUTCToLocalTime = (utcDateString: string): string => {
    try {
      // The server is in UTC timezone, so database dates are in UTC
      // Format: "yyyy-MM-dd HH:mm:ss" (UTC time)
      let dateString = utcDateString;
      
      // Convert space to 'T' and add 'Z' to make it explicit UTC
      if (dateString.includes(' ')) {
        dateString = dateString.replace(' ', 'T') + 'Z';
      } else if (dateString.includes('T') && !dateString.includes('Z')) {
        dateString = dateString + 'Z';
      }
      
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', utcDateString);
        return utcDateString;
      }
      
      // Return formatted date in local timezone
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error converting date:', utcDateString, error);
      return utcDateString;
    }
  };

  const handleSort = (property: keyof Bill) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleColumnFilterChange = (field: string, value: any) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnFilterClear = (field: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  const getFilteredBills = () => {
    return [...bills].sort((a, b) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];
      
      if (orderBy === 'createdat') {
        aValue = new Date(aValue.replace(' ', 'T')).getTime();
        bValue = new Date(bValue.replace(' ', 'T')).getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const handleDeleteBill = async (billId: number) => {
    if (!window.confirm('Are you sure you want to delete this bill?')) {
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.delete(`${API_BASE_URL}/bills/${billId}`, {
        headers: { Authorization: token }
      });
      
      setShowBillDetailsDialog(false);
      fetchBills();
    } catch (error) {
      console.error('Error deleting bill:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrintBill = (bill: Bill) => {
    // Set margins (5mm on each side)
    const leftMargin = 14.17; // 5mm in points
    const rightMargin = 14.17;
    const contentWidth = 198.43; // 80mm - 10mm margins

    // Calculate logo dimensions
    const logoWidth = contentWidth * 0.8; // 80% of content width
    const logoHeight = (logoWidth * 40) / 60; // Maintain aspect ratio (60:40)
    
    // Calculate required height based on content
    const itemsHeight = bill.items.length * 15; // 15pt per item
    const baseHeight = 320; // Increased from 280 to 320 to accommodate new fields
    const totalHeight = baseHeight + itemsHeight + logoHeight; // Total required height
    
    // Create PDF with 80mm width and calculated height
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: [226.77, totalHeight] // 80mm width, dynamic height
    });
    
    // Helper function to center text
    const centerText = (text: string, y: number) => {
      const textWidth = doc.getStringUnitWidth(text) * doc.getFontSize() / doc.internal.scaleFactor;
      const x = (contentWidth - textWidth) / 2 + leftMargin;
      doc.text(text, x, y);
    };

    // Helper function to draw dashed line
    const drawDashedLine = (y: number) => {
      const dashWidth = 5;
      const dashSpace = 2;
      let x = leftMargin;
      while (x < contentWidth + leftMargin) {
        doc.line(x, y, x + dashWidth, y);
        x += dashWidth + dashSpace;
      }
    };

    // Add logo
    const logoX = (contentWidth - logoWidth) / 2 + leftMargin;
    doc.addImage('/SAWLOGO.png', 'PNG', logoX, 5, logoWidth, logoHeight);
    
    // Add NTN/STRN line
    doc.setFontSize(8);
    centerText(`NTN & STRN : ${NTN}`, logoHeight + 20);
    
    // Draw dashed line
    drawDashedLine(logoHeight + 30);
    
    // Add bill details
    doc.setFontSize(8);
    doc.text(`Bill No: ${bill.id}`, leftMargin, logoHeight + 45);
    doc.text(`Date: ${convertUTCToLocalTime(bill.createdat)}`, leftMargin, logoHeight + 60);
    doc.text(`Shop: ${bill.shopname}`, leftMargin, logoHeight + 75);
    doc.text(`Order Type: ${bill.orderType}`, leftMargin, logoHeight + 90);
    doc.text(`Payment Method: ${bill.paymentMethod}`, leftMargin, logoHeight + 105);
    
    // Draw dashed line
    drawDashedLine(logoHeight + 115);
    
    // Add items header
    doc.setFontSize(8);
    doc.text('Item', leftMargin, logoHeight + 130);
    doc.text('Qty', leftMargin + 80, logoHeight + 130);
    doc.text('Price', leftMargin + 110, logoHeight + 130);
    doc.text('Disc%', leftMargin + 140, logoHeight + 130);
    doc.text('Total', leftMargin + 170, logoHeight + 130);
    
    // Add items
    let y = logoHeight + 145;
    let subtotal = 0;
    
    bill.items.forEach(item => {
      const itemTotal = item.quantity * item.pricePerUnit * (1 - item.discount / 100);
      subtotal += itemTotal;
      
      doc.text(item.itemname.substring(0, 15), leftMargin, y);
      doc.text(item.quantity.toString(), leftMargin + 80, y);
      doc.text(item.pricePerUnit.toFixed(2), leftMargin + 110, y);
      doc.text(item.discount.toString(), leftMargin + 140, y);
      doc.text(itemTotal.toFixed(2), leftMargin + 170, y);
      y += 15;
    });
    
    // Add subtotal
    doc.setFontSize(8);
    doc.text('Subtotal:', leftMargin, y + 10);
    doc.text(subtotal.toFixed(2), leftMargin + 170, y + 10);

    if (Number(bill.cardDiscountAmount || 0) > 0) {
      doc.text(`Card Discount (${bill.cardDiscountBankName || 'Card'}):`, leftMargin, y + 25);
      doc.text(`-${Number(bill.cardDiscountAmount).toFixed(2)}`, leftMargin + 150, y + 25);
    }

    // Add delivery charges
    doc.text('Delivery Charges:', leftMargin, y + 40);
    doc.text(bill.deliveryCharges.toFixed(2), leftMargin + 150, y + 40);
    
    // Add GST
    doc.text(`GST (18%) ${bill.applyGstOnDelivery ? '(incl. delivery)' : '(excl. delivery)'}:`, leftMargin, y + 55);
    doc.text(bill.gstAmount.toFixed(2), leftMargin + 150, y + 55);
    
    // Add total with all charges
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Total Amount:', leftMargin, y + 70);
    doc.text(getBillTotal(bill).toFixed(2), leftMargin + 150, y + 70);
    
    // Draw dashed line
    drawDashedLine(y + 85);
    
    // Add footer
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    centerText('Thank you for your purchase!', y + 115);
    
    doc.save(`bill-${bill.id}.pdf`);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ width: '100%' }}>
      {isLoadingMetadata ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <ReportFilterBar
          configs={columnFilterConfigs}
          values={columnFilters}
          onChange={handleColumnFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          searchDisabled={!hasAtLeastOneFilter(columnFilters)}
          isSearching={isLoadingBills}
          resultCount={bills.length}
          hasSearched={hasSearched}
        />
      )}
      <TableContainer 
        component={Paper} 
        sx={{ 
          position: 'relative',
          minHeight: { xs: 220, md: 180 },
          maxHeight: { xs: 'calc(100vh - 220px)', md: 'calc(100vh - 320px)' },
          '& .MuiTableCell-root': {
            padding: { xs: '8px 10px', md: '8px 16px' },
            fontSize: '0.875rem',
            whiteSpace: 'nowrap'
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '80px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleSort('id')}
                  >
                    ID
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[0]}
                      value={columnFilters.id}
                      onChange={(value) => handleColumnFilterChange('id', value)}
                      onClear={() => handleColumnFilterClear('id')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'createdat'}
                    direction={orderBy === 'createdat' ? order : 'asc'}
                    onClick={() => handleSort('createdat')}
                  >
                    Date
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[1]}
                      value={columnFilters.createdat}
                      onChange={(value) => handleColumnFilterChange('createdat', value)}
                      onClear={() => handleColumnFilterClear('createdat')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'shopname'}
                    direction={orderBy === 'shopname' ? order : 'asc'}
                    onClick={() => handleSort('shopname')}
                  >
                    Shop
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[2]}
                      value={columnFilters.shopname}
                      onChange={(value) => handleColumnFilterChange('shopname', value)}
                      onClear={() => handleColumnFilterClear('shopname')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'orderType'}
                    direction={orderBy === 'orderType' ? order : 'asc'}
                    onClick={() => handleSort('orderType')}
                  >
                    Order Type
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[3]}
                      value={columnFilters.orderType}
                      onChange={(value) => handleColumnFilterChange('orderType', value)}
                      onClear={() => handleColumnFilterClear('orderType')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'paymentMethod'}
                    direction={orderBy === 'paymentMethod' ? order : 'asc'}
                    onClick={() => handleSort('paymentMethod')}
                  >
                    Payment Method
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[4]}
                      value={columnFilters.paymentMethod}
                      onChange={(value) => handleColumnFilterChange('paymentMethod', value)}
                      onClear={() => handleColumnFilterClear('paymentMethod')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TableSortLabel
                    active={orderBy === 'totalAmount'}
                    direction={orderBy === 'totalAmount' ? order : 'asc'}
                    onClick={() => handleSort('totalAmount')}
                  >
                    Total Amount
                  </TableSortLabel>
                  {!isMobile && (
                    <ColumnFilter
                      config={columnFilterConfigs[5]}
                      value={columnFilters.totalAmount}
                      onChange={(value) => handleColumnFilterChange('totalAmount', value)}
                      onClear={() => handleColumnFilterClear('totalAmount')}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingBills ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                      Loading results...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : getFilteredBills().length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {hasSearched ? 'No results found' : 'Select filters and click Search'}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {getFilteredBills().map((bill) => (
                  <TableRow key={bill.id}>
                    <TableCell>{bill.id}</TableCell>
                    <TableCell>{convertUTCToLocalTime(bill.createdat)}</TableCell>
                    <TableCell>{bill.shopname}</TableCell>
                    <TableCell>{bill.orderType}</TableCell>
                    <TableCell>{bill.paymentMethod}</TableCell>
                    <TableCell align="right">
                      PKR{getBillTotal(bill).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => {
                          setSelectedBill(bill);
                          setShowBillDetailsDialog(true);
                        }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ 
                  position: 'sticky', 
                  bottom: 0, 
                  zIndex: 2,
                  backgroundColor: 'primary.main',
                  '& .MuiTableCell-root': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 'bold',
                    borderTop: '2px solid',
                    borderTopColor: 'primary.dark'
                  }
                }}>
                  <TableCell colSpan={5} align="right">Total:</TableCell>
                  <TableCell align="right">
                    PKR{getFilteredBills().reduce((sum, bill) => sum + getBillTotal(bill), 0).toFixed(2)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bill Details Dialog */}
      <Dialog 
        open={showBillDetailsDialog} 
        onClose={() => setShowBillDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bill Details</DialogTitle>
        <DialogContent>
          {selectedBill && (
            <>
              <Typography variant="subtitle1">Bill ID: {selectedBill.id}</Typography>
              <Typography variant="subtitle1">Shop: {selectedBill.shopname}</Typography>
              <Typography variant="subtitle1">Date: {convertUTCToLocalTime(selectedBill.createdat)}</Typography>
              <Typography variant="subtitle1">Order Type: {selectedBill.orderType}</Typography>
              <Typography variant="subtitle1">Payment Method: {selectedBill.paymentMethod}</Typography>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item Name</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Price/Unit</TableCell>
                      <TableCell align="right">Discount (%)</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedBill.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.itemname}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">PKR{item.pricePerUnit.toFixed(2)}</TableCell>
                        <TableCell align="right">{item.discount}%</TableCell>
                        <TableCell align="right">
                          PKR{(item.quantity * item.pricePerUnit * (1 - item.discount / 100)).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4} align="right">Subtotal:</TableCell>
                      <TableCell align="right">
                        PKR{getItemsSubtotal(selectedBill).toFixed(2)}
                      </TableCell>
                    </TableRow>
                    {Number(selectedBill.cardDiscountAmount || 0) > 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="right">
                          Card Discount ({selectedBill.cardDiscountBankName}):
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>
                          - PKR{Number(selectedBill.cardDiscountAmount).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell colSpan={4} align="right">Delivery Charges:</TableCell>
                      <TableCell align="right">
                        PKR{selectedBill.deliveryCharges.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={4} align="right">
                        GST (18%) {selectedBill.applyGstOnDelivery ? '(including delivery)' : '(excluding delivery)'}:
                      </TableCell>
                      <TableCell align="right">
                        PKR{selectedBill.gstAmount.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ fontWeight: 'bold' }}>
                      <TableCell colSpan={4} align="right">Total Amount:</TableCell>
                      <TableCell align="right">
                        PKR{getBillTotal(selectedBill).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {hasPermission('delete-bill') && (
            <Button 
              variant="contained" 
              color="error" 
              onClick={() => selectedBill && handleDeleteBill(selectedBill.id)}
              disabled={isSubmitting}
            >
              Delete Bill
            </Button>
          )}
          <Button 
            variant="outlined" 
            onClick={() => selectedBill && handlePrintBill(selectedBill)}
          >
            Print Bill
          </Button>
          <Button onClick={() => setShowBillDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bills;
